import { createRouter, createWebHistory } from 'vue-router'
import { installGuards } from './guards'
import type { Permission } from '@/types/permission'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth?: boolean
    requiresPlatformAuth?: boolean
    focusMode?: boolean
    /** Route is only reachable when the current role has this permission. */
    permission?: Permission
    /** Reachable before onboarding is finished (the onboarding flow itself). */
    allowBeforeOnboarding?: boolean
  }
}

const MarketingLayout = () => import('@/layouts/MarketingLayout.vue')
const AuthLayout = () => import('@/layouts/AuthLayout.vue')
const AppShellLayout = () => import('@/layouts/AppShellLayout.vue')
const KioskLayout = () => import('@/layouts/KioskLayout.vue')
const PublicLayout = () => import('@/layouts/PublicLayout.vue')
const PlatformAdminLayout = () => import('@/layouts/PlatformAdminLayout.vue')
const PlatformAuthLayout = () => import('@/layouts/PlatformAuthLayout.vue')
const OnboardingLayout = () => import('@/layouts/OnboardingLayout.vue')

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      component: MarketingLayout,
      children: [
        {
          path: '',
          name: 'landing',
          component: () => import('@/views/marketing/LandingView.vue'),
          meta: { title: 'Toți clienții tăi. Toată comunicarea.' },
        },
        {
          path: 'cere-demo',
          name: 'demo-request',
          component: () => import('@/views/marketing/DemoRequestView.vue'),
          meta: { title: 'Cere un demo' },
        },
      ],
    },
    {
      path: '/autentificare',
      component: AuthLayout,
      children: [
        {
          path: '',
          name: 'login',
          component: () => import('@/views/auth/LoginView.vue'),
          meta: { title: 'Conectare' },
        },
      ],
    },
    {
      path: '/parola-uitata',
      component: AuthLayout,
      children: [
        {
          path: '',
          name: 'forgot-password',
          component: () => import('@/views/auth/ForgotPasswordView.vue'),
          meta: { title: 'Ai uitat parola?' },
        },
      ],
    },
    {
      path: '/inregistrare',
      component: AuthLayout,
      children: [
        {
          path: '',
          name: 'register',
          component: () => import('@/views/auth/RegisterView.vue'),
          meta: { title: 'Creează-ți contul' },
        },
      ],
    },
    {
      path: '/acord/:token',
      component: PublicLayout,
      children: [
        {
          path: '',
          name: 'public-consent-signing',
          component: () => import('@/views/public/PublicConsentSigningView.vue'),
          meta: { title: 'Acord de comunicare' },
        },
      ],
    },
    {
      path: '/dezabonare/:customerId/:signature',
      component: PublicLayout,
      children: [
        {
          path: '',
          name: 'unsubscribe',
          component: () => import('@/views/public/UnsubscribeView.vue'),
          meta: { title: 'Oprește mesajele' },
        },
      ],
    },
    {
      path: '/tablet',
      component: KioskLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'kiosk',
          component: () => import('@/views/kiosk/KioskView.vue'),
          meta: { title: 'Client nou' },
        },
      ],
    },
    // The design-system page is a development tool, not part of the product:
    // in a build it does not exist, so nobody can stumble onto it.
    ...(import.meta.env.DEV
      ? [
          {
            path: '/ui-kit',
            name: 'ui-kit',
            component: () => import('@/views/dev/UiKitView.vue'),
            meta: { title: 'UI Kit' },
          },
        ]
      : []),
    {
      path: '/',
      component: AppShellLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/views/dashboard/DashboardView.vue'),
          meta: { title: 'Panou principal' },
        },
        {
          path: 'clienti',
          name: 'customers',
          component: () => import('@/views/customers/CustomersListView.vue'),
          meta: { title: 'Clienți', permission: 'customers.view' },
        },
        {
          path: 'clienti/import',
          name: 'customers-import',
          component: () => import('@/views/customers/CustomerImportView.vue'),
          meta: { title: 'Importă clienți', focusMode: true, permission: 'customers.import' },
        },
        {
          path: 'clienti/:id',
          name: 'customer-profile',
          component: () => import('@/views/customers/CustomerProfileView.vue'),
          meta: { title: 'Profil client', permission: 'customers.view' },
          props: true,
        },
        {
          path: 'acorduri',
          name: 'consents',
          component: () => import('@/views/consents/ConsentsListView.vue'),
          meta: { title: 'Acorduri', permission: 'customers.view' },
        },
        {
          path: 'campanii',
          name: 'campaigns',
          component: () => import('@/views/campaigns/CampaignsListView.vue'),
          meta: { title: 'Campanii', permission: 'campaigns.view' },
        },
        {
          path: 'campanii/noua',
          name: 'campaign-wizard',
          component: () => import('@/views/campaigns/CampaignWizardView.vue'),
          meta: { title: 'Campanie nouă', focusMode: true, permission: 'campaigns.create' },
        },
        {
          path: 'mesaje',
          name: 'messages',
          component: () => import('@/views/messages/MessagesListView.vue'),
          meta: { title: 'Mesaje', permission: 'campaigns.view' },
        },
        {
          path: 'conversatii',
          name: 'conversations',
          component: () => import('@/views/conversations/ConversationsView.vue'),
          meta: { title: 'Conversații', permission: 'customers.view' },
        },
        {
          path: 'suport',
          name: 'support',
          component: () => import('@/views/support/SupportView.vue'),
          meta: { title: 'Suport' },
        },
        {
          path: 'site',
          name: 'sites',
          component: () => import('@/views/sites/SitesView.vue'),
          meta: { title: 'Site-ul tău' },
        },
        {
          path: 'analytics',
          name: 'web-analytics',
          component: () => import('@/views/sites/WebAnalyticsView.vue'),
          meta: { title: 'Analytics', permission: 'analytics.view' },
        },
        {
          path: 'chatbot',
          name: 'bots',
          component: () => import('@/views/bots/BotsView.vue'),
          meta: { title: 'Chatbot', permission: 'automations.manage' },
        },
        {
          path: 'chatbot/:id',
          name: 'bot-editor',
          component: () => import('@/views/bots/BotEditorView.vue'),
          props: true,
          meta: { title: 'Editor chatbot', permission: 'automations.manage' },
        },
        {
          path: 'sabloane',
          name: 'templates',
          component: () => import('@/views/templates/TemplatesListView.vue'),
          meta: { title: 'Șabloane', permission: 'campaigns.view' },
        },
        {
          path: 'sabloane/email/:id',
          name: 'email-template-editor',
          component: () => import('@/views/templates/EmailTemplateEditorView.vue'),
          meta: { title: 'Editor email', focusMode: true, permission: 'campaigns.view' },
          props: true,
        },
        {
          path: 'sabloane/sms/:id',
          name: 'sms-template-editor',
          component: () => import('@/views/templates/SmsTemplateEditorView.vue'),
          meta: { title: 'Editor SMS', focusMode: true, permission: 'campaigns.view' },
          props: true,
        },
        {
          path: 'grupuri',
          name: 'segments',
          component: () => import('@/views/segments/SegmentsListView.vue'),
          meta: { title: 'Grupuri de clienți', permission: 'customers.view' },
        },
        {
          path: 'statistici',
          name: 'analytics',
          component: () => import('@/views/analytics/AnalyticsView.vue'),
          meta: { title: 'Statistici', permission: 'analytics.view' },
        },
        {
          path: 'documente',
          name: 'documents',
          component: () => import('@/views/documents/DocumentsListView.vue'),
          meta: { title: 'Documente', permission: 'documents.view' },
        },
        {
          path: 'automatizari',
          name: 'automations',
          component: () => import('@/views/automations/AutomationsListView.vue'),
          meta: { title: 'Automatizări', permission: 'automations.manage' },
        },
        {
          path: 'automatizari/:id',
          name: 'automation-editor',
          component: () => import('@/views/automations/AutomationEditorView.vue'),
          meta: { title: 'Automatizare', focusMode: true, permission: 'automations.manage' },
          props: true,
        },
        {
          path: 'echipa',
          name: 'team',
          component: () => import('@/views/team/TeamView.vue'),
          meta: { title: 'Echipă', permission: 'team.manage' },
        },
        {
          path: 'setari',
          name: 'settings',
          component: () => import('@/views/settings/SettingsView.vue'),
          meta: { title: 'Setări' },
        },
        {
          path: 'jurnal',
          name: 'audit-log',
          component: () => import('@/views/audit/AuditLogView.vue'),
          meta: { title: 'Jurnal de activitate', permission: 'audit.view' },
        },
        {
          path: 'acces-restrictionat',
          name: 'forbidden',
          component: () => import('@/views/errors/ForbiddenView.vue'),
          meta: { title: 'Acces restricționat' },
        },
      ],
    },
    {
      path: '/bun-venit',
      component: OnboardingLayout,
      meta: { requiresAuth: true, allowBeforeOnboarding: true },
      children: [
        {
          path: '',
          name: 'onboarding',
          component: () => import('@/views/onboarding/OnboardingView.vue'),
          meta: { title: 'Bun venit' },
        },
      ],
    },
    {
      path: '/ereach-admin/autentificare',
      component: PlatformAuthLayout,
      children: [
        {
          path: '',
          name: 'platform-login',
          component: () => import('@/views/admin/PlatformLoginView.vue'),
          meta: { title: 'Conectare EReach Admin' },
        },
      ],
    },
    {
      path: '/ereach-admin',
      component: PlatformAdminLayout,
      meta: { requiresPlatformAuth: true },
      children: [
        {
          path: '',
          name: 'platform-dashboard',
          component: () => import('@/views/admin/PlatformDashboardView.vue'),
          meta: { title: 'Panou principal' },
        },
        {
          path: 'companii',
          name: 'platform-companies',
          component: () => import('@/views/admin/CompaniesListView.vue'),
          meta: { title: 'Companii' },
        },
        {
          path: 'companii/:id',
          name: 'platform-company-detail',
          component: () => import('@/views/admin/CompanyDetailView.vue'),
          meta: { title: 'Detalii companie' },
          props: true,
        },
        {
          path: 'utilizatori',
          name: 'platform-users',
          component: () => import('@/views/admin/PlatformUsersView.vue'),
          meta: { title: 'Utilizatori' },
        },
        {
          path: 'abonamente',
          name: 'platform-subscriptions',
          component: () => import('@/views/admin/SubscriptionsView.vue'),
          meta: { title: 'Abonamente' },
        },
        {
          path: 'planuri',
          name: 'platform-plans',
          component: () => import('@/views/admin/PlansView.vue'),
          meta: { title: 'Planuri și prețuri' },
        },
        {
          path: 'usage',
          name: 'platform-usage',
          component: () => import('@/views/admin/UsageView.vue'),
          meta: { title: 'Usage' },
        },
        {
          path: 'mesaje',
          name: 'platform-messages',
          component: () => import('@/views/admin/PlatformMessagesView.vue'),
          meta: { title: 'Mesaje', permission: 'campaigns.view' },
        },
        {
          path: 'integrari',
          name: 'platform-integrations',
          component: () => import('@/views/admin/PlatformIntegrationsView.vue'),
          meta: { title: 'Integrări' },
        },
        {
          path: 'erori',
          name: 'platform-errors',
          component: () => import('@/views/admin/SystemLogsView.vue'),
          meta: { title: 'Erori' },
          props: { errorsOnly: true },
        },
        {
          path: 'loguri',
          name: 'platform-logs',
          component: () => import('@/views/admin/SystemLogsView.vue'),
          meta: { title: 'Loguri' },
          props: { errorsOnly: false },
        },
        {
          path: 'tichete',
          name: 'platform-tickets',
          component: () => import('@/views/admin/TicketsListView.vue'),
          meta: { title: 'Tichete' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/errors/NotFoundView.vue'),
      meta: { title: 'Pagina nu a fost găsită' },
    },
  ],
})

installGuards(router)

export default router
